import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { animeRoles } from "@/mocks/roles";
import { animeNews } from "@/mocks/news";

const navItems = [
  { label: "首页", to: "/" },
  { label: "角色资料", to: "/roles" },
  { label: "动漫资讯", to: "/news" },
  { label: "社区交流", to: "/community" },
  { label: "关于我们", to: "/about" }
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return { roles: [], news: [] };
    return {
      roles: animeRoles
        .filter((r) => r.name.toLowerCase().includes(q) || r.work.toLowerCase().includes(q))
        .slice(0, 4),
      news: animeNews.filter((n) => n.title.toLowerCase().includes(q)).slice(0, 3)
    };
  }, [keyword]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    setSearchOpen(false);
    navigate(`/roles?q=${encodeURIComponent(q)}`);
  };

  const hasResults = results.roles.length > 0 || results.news.length > 0;

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-3-fill text-lg dark:text-foreground-950"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl text-foreground-950">萝莉</span>
            <span className="text-[10px] tracking-wider text-foreground-500">ANIME PORTAL</span>
          </span>
        </Link>

        <nav className="mx-auto hidden items-center gap-1 lg:flex">
          {navItems.map((item) => {
            const active =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <div ref={searchRef} className="relative hidden md:block">
            <form onSubmit={handleSubmit}>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-300">
                <i className="ri-search-line text-sm text-foreground-500"></i>
                <input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="搜索角色、资讯关键词"
                  className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none xl:w-52"
                />
              </div>
            </form>

            {searchOpen && keyword.trim() && (
              <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-background-200 bg-background-50 p-2 shadow-none">
                {hasResults ? (
                  <>
                    {results.roles.length > 0 && (
                      <div className="mb-1">
                        <p className="px-2 py-1 text-xs text-foreground-500">角色</p>
                        {results.roles.map((r) => (
                          <Link
                            key={r.id}
                            to={`/role/${r.id}`}
                            className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                          >
                            <div className="h-9 w-9 overflow-hidden rounded-md bg-background-100">
                              <img src={r.cover} alt={r.name} className="h-full w-full object-top" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm text-foreground-900">{r.name}</p>
                              <p className="truncate text-xs text-foreground-500">{r.work}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                    {results.news.length > 0 && (
                      <div>
                        <p className="px-2 py-1 text-xs text-foreground-500">资讯</p>
                        {results.news.map((n) => (
                          <Link
                            key={n.id}
                            to={`/news/${n.id}`}
                            className="block rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                          >
                            <p className="line-clamp-1 text-sm text-foreground-900">{n.title}</p>
                            <p className="text-xs text-foreground-500">{n.date}</p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="px-2 py-6 text-center text-sm text-foreground-500">未找到相关内容</p>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden cursor-pointer"
            aria-label="打开菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="px-4 py-3">
            <form onSubmit={handleSubmit} className="mb-3">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
                <i className="ri-search-line text-sm text-foreground-500"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索角色、资讯关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
                />
              </div>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}